import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { resolverSocketUrl } from '../services/urls';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { AuthContext } from './AuthContext';

export const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const { user } = useContext(AuthContext);

  // Listeners registrados por las pantallas via useOnReconnect() para
  // re-disparar sus fetch de datos apenas vuelve la conexión (no sólo en
  // el próximo polling), sin acoplar cada pantalla al socket directamente.
  const recoveryListenersRef = useRef(new Set());
  const recoveryDebounceRef = useRef(null);
  const wasOfflineRef = useRef(false);

  // 'online' del browser y 'reconnect' del socket pueden llegar juntos;
  // debounce para no disparar los refetch duplicados.
  const notifyRecovered = useCallback(() => {
    if (recoveryDebounceRef.current) clearTimeout(recoveryDebounceRef.current);
    recoveryDebounceRef.current = setTimeout(() => {
      recoveryListenersRef.current.forEach((fn) => {
        try { fn(); } catch (e) { console.error('useOnReconnect listener falló:', e); }
      });
    }, 500);
  }, []);

  const subscribeReconnect = useCallback((fn) => {
    recoveryListenersRef.current.add(fn);
    return () => recoveryListenersRef.current.delete(fn);
  }, []);

  // Function to manually reconnect
  const manualReconnect = useCallback(() => {
    if (socket) {
      console.log('Manually reconnecting socket...');
      socket.connect();
    }
  }, [socket]);

  // Aviso de conectividad a nivel navegador (independiente del socket/login):
  // es la señal más directa de "se cortó/volvió Internet".
  useEffect(() => {
    const handleBrowserOffline = () => {
      wasOfflineRef.current = true;
      toast('Sin conexión, reintentando…', { id: 'connectivity-status', icon: '📴', duration: Infinity });
    };
    const handleBrowserOnline = () => {
      if (wasOfflineRef.current) {
        // Mismo id que el toast de "Sin conexión" (lo reemplaza en su lugar),
        // pero react-hot-toast mergea `{...toastViejo, ...toastNuevo}` — sin
        // `duration` explícito acá, hereda el `duration: Infinity` de aquel
        // toast y este nunca se auto-descarta.
        toast.success('Conexión restablecida', { id: 'connectivity-status', duration: 4000 });
        wasOfflineRef.current = false;
      }
      notifyRecovered();
    };
    window.addEventListener('offline', handleBrowserOffline);
    window.addEventListener('online', handleBrowserOnline);
    return () => {
      window.removeEventListener('offline', handleBrowserOffline);
      window.removeEventListener('online', handleBrowserOnline);
    };
  }, [notifyRecovered]);

  useEffect(() => {
    // Only connect if the user is authenticated
    if (user) {
      // Determinar URL del socket basado en el entorno
      const getSocketUrl = () => resolverSocketUrl(
        window.location.hostname, window.location.origin, process.env.REACT_APP_SOCKET_URL);
      
      const socketUrl = getSocketUrl();
      
      console.log('Connecting to socket at:', socketUrl);
      
      // Create socket connection with automatic reconnection settings and explicit path
      const socketInstance = io(socketUrl, {
        path: '/socket.io/',
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        randomizationFactor: 0.5,
        timeout: 20000,
        secure: socketUrl.startsWith('https'),
        transports: ['websocket', 'polling']  // Preferir WebSocket, con fallback a polling
      });
      
      socketInstance.on('connect', () => {
        console.log('Socket connected');
        setConnected(true);
        setReconnecting(false);
        setReconnectAttempts(0);
      });
      
      socketInstance.on('disconnect', () => {
        console.log('Socket disconnected');
        setConnected(false);
      });
      
      socketInstance.on('reconnect_attempt', (attempt) => {
        console.log(`Socket reconnection attempt ${attempt}`);
        setReconnecting(true);
        setReconnectAttempts(attempt);
      });
      
      socketInstance.on('reconnect', (attempt) => {
        console.log(`Socket reconnected after ${attempt} attempts`);
        setConnected(true);
        setReconnecting(false);
        setReconnectAttempts(0);
        notifyRecovered();
      });
      
      socketInstance.on('reconnect_failed', () => {
        console.log('Socket reconnection failed');
        setReconnecting(false);
      });
      
      socketInstance.on('connect_error', (error) => {
        console.error('Connection error:', error);
        setConnected(false);
      });
      
      setSocket(socketInstance);

      // Reconnect when tab becomes visible again (browsers suspend sockets in background)
      const handleVisibility = () => {
        if (document.visibilityState === 'visible' && !socketInstance.connected) {
          console.log('Tab visible again, forcing socket reconnect');
          socketInstance.connect();
        }
      };
      // Reconnect when network comes back online
      const handleOnline = () => {
        if (!socketInstance.connected) {
          console.log('Network online, forcing socket reconnect');
          socketInstance.connect();
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('online', handleOnline);

      // Cleanup on unmount
      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('online', handleOnline);
        socketInstance.disconnect();
      };
    }
  }, [user, notifyRecovered]);

  return (
    <SocketContext.Provider value={{
      socket,
      connected,
      reconnecting,
      reconnectAttempts,
      subscribeReconnect,
      reconnect: manualReconnect
    }}>
      {children}
    </SocketContext.Provider>
  );
};